import React from 'react';
import log from '@wix/wix-log';
import type { NextPage } from 'next';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import _ from 'lodash';
import { web } from '@wix/wix-common-aspects';
import { WixDesignSystemProvider, Box } from '@wix/design-system';
import { Service } from '@wix/ambassador-marketplace-categories-v1-category/types';
import { Type } from '@wix/ambassador-partners-marketplace-v1-providerstate/types';
import {
  WixNextApiRequest,
  WixNextApiResponse,
  IParams,
  SEOProps,
  Template as TemplateData,
  EDITOR_TYPES,
  ReviewsInfo,
  ReviewData,
} from '@common/types';
import {
  ENABLE_TEMPLATE_VIEW_PROFILE_LINKS_TO_AGENCY_PAGE,
  INVALID_ARGUMENT,
  TEMPLATE_NOT_FOUND,
} from '@utils/constants';

import { getWixContext } from '@utils/async-store';
import createTemplateSeoAPI from '@services/TemplateSeoAPI';
import { Language } from '@common/types';
import { TemplateContextProvider } from '@components/Contexts/TemplateContext';
import { HeaderContextProvider } from '@components/Contexts/HeaderContext';
import { BiSessionContextProvider } from '@components/Contexts/BiSessionContext';
import { CurrencyContextProvider } from '@components/Contexts/CurrencyContext';
import { ModalsContextProvider } from '@components/Contexts/ModalsContext';
import Template from '@components/Template';
import { viewTemplatePath } from '@components/Hooks/useRouter';
import useMobileMediaQuery from '@components/Hooks/useMobileMediaQuery';
import { getPageLanguage } from '@app/utils';
import createInitialStateService from '@services/InitialStateService';

const DynamicNotFoundErrorState = dynamic(
  () => import('@components/Template/ErrorStates/NotFoundErrorState'),
  {
    loading: () => <></>,
  },
);

const DynamicTechnicalErrorState = dynamic(
  () => import('@components/Template/ErrorStates/TechnicalErrorState'),
  {
    loading: () => <></>,
  },
);

const DynamicTemplatePageHeader = dynamic(
  () => import('@components/Template/TemplatePageHeader'),
  {
    loading: () => <></>,
  },
);

function ErrorState({
  error,
  isMobileUserAgent,
}: {
  error: string;
  isMobileUserAgent: boolean;
}) {
  const isMobileView = useMobileMediaQuery(isMobileUserAgent);
  return (
    <>
      <DynamicTemplatePageHeader />
      <Box height={`calc(100vh - ${isMobileView ? '54px' : '72px'})`}>
        {error.includes(INVALID_ARGUMENT) || error === TEMPLATE_NOT_FOUND ? (
          <DynamicNotFoundErrorState />
        ) : (
          <DynamicTechnicalErrorState />
        )}
      </Box>
    </>
  );
}

const TemplatePage: NextPage<{
  template: TemplateData;
  reviewsInfo: ReviewsInfo;
  purchaseCount: number;
  services: Array<Service>;
  categoriesForHeader: Array<Service> | undefined;
  language: string;
  languages: Array<Language>;
  enabled: boolean;
  isRenderedInModal: boolean;
  isMobileUserAgent: boolean;
  error: string;
  host: string;
  origin?: string;
  entry?: string;
  seo: SEOProps;
  isEligibleToGiveReview: boolean;
  userAlreadyLeftReview: boolean;
  userCurrency: string;
  userReviewForTemplate: ReviewData | null;
  currencyConversionRate: number;
  taxRate: number;
  isInPriceTaxInclusiveCountry: boolean;
}> = ({
  template,
  reviewsInfo,
  purchaseCount,
  services,
  categoriesForHeader,
  languages,
  language,
  isMobileUserAgent,
  host,
  origin,
  entry,
  error,
  seo,
  isEligibleToGiveReview,
  userAlreadyLeftReview,
  userCurrency,
  userReviewForTemplate,
  currencyConversionRate,
  taxRate,
  isInPriceTaxInclusiveCountry,
}) => {
  const isProd = process.env.NODE_ENV === 'production';

  const TemplateHeader = () => (
    <Head>
      <title>{seo.title}</title>
      {_.map(seo.links, (attr, index) =>
        attr.rel === 'canonical' ? null : (
          <link
            {...(attr.hrefLang && { hrefLang: attr.hrefLang })}
            rel={attr.rel}
            href={attr.href}
            key={`lang-${index}`}
          />
        ),
      )}
      {_.map(seo.metaTags, (attr, index) => (
        <meta {...attr} key={`meta-${index}`} />
      ))}
      <link
        rel="canonical"
        href={_.get(_.find(seo.links, { rel: 'canonical' }), 'href')}
      ></link>
      {template?.id && (
        <link
          rel="prefetch"
          href={`${viewTemplatePath}/${template?.id}`}
          as="document"
        ></link>
      )}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: seo.structureData }}
      ></script>
    </Head>
  );

  const showErrorState = error !== undefined;

  return (
    <>
      <TemplateHeader />
      <BiSessionContextProvider>
        <CurrencyContextProvider
          currencyData={{
            currencyCode: userCurrency,
            conversionRate: currencyConversionRate,
          }}
          taxRate={taxRate}
          isInPriceTaxInclusiveCountry={isInPriceTaxInclusiveCountry}
        >
          <HeaderContextProvider
            services={categoriesForHeader || services}
            host={host}
            origin={origin}
            entry={entry}
          >
            <ModalsContextProvider host={host}>
              <TemplateContextProvider
                language={language}
                languages={languages}
                reviewsInfo={reviewsInfo}
                purchaseCount={purchaseCount}
                template={template}
                isMobileUserAgent={isMobileUserAgent}
                isEligibleToGiveReview={isEligibleToGiveReview}
                userAlreadyLeftReview={userAlreadyLeftReview}
                userReviewForTemplate={userReviewForTemplate}
              >
                <WixDesignSystemProvider features={{ newColorsBranding: true }}>
                  {showErrorState ? (
                    <ErrorState
                      error={error}
                      isMobileUserAgent={isMobileUserAgent}
                    />
                  ) : (
                    <Template />
                  )}
                </WixDesignSystemProvider>
              </TemplateContextProvider>
            </ModalsContextProvider>
          </HeaderContextProvider>
        </CurrencyContextProvider>
      </BiSessionContextProvider>
    </>
  );
};

export async function getServerSideProps({
  req,
  res,
  query,
  params,
}: {
  req: WixNextApiRequest;
  res: WixNextApiResponse;
  query: IParams;
  params: any;
}) {
  const wixContext = getWixContext();
  const initialStateService = createInitialStateService(req.aspects, res);
  const petriServiceAPI = wixContext.serviceFactory.petriServiceAPI();
  const isCreatorInfoLinksToAgencyPageEnabled =
    await petriServiceAPI.isExperimentEnabled(
      ENABLE_TEMPLATE_VIEW_PROFILE_LINKS_TO_AGENCY_PAGE,
    );

  const { id } = params;
  const { entry, origin } = query;
  const host = `https://${req.get('host')}`;

  const servicesAPI = wixContext.serviceFactory.servicesAPI();
  const reviewsAPI = wixContext.serviceFactory.reviewsAPI();
  const providersAPI = wixContext.serviceFactory.providersAPI();
  const languagesAPI = wixContext.serviceFactory.languagesAPI();
  const templatesAPI = wixContext.serviceFactory.templatesAPI();
  const providerStatesAPI = wixContext.serviceFactory.providerStatesAPI();
  const templateListingAPI = wixContext.serviceFactory.templateListingAPI();
  const transactionsServiceAPI =
    wixContext.serviceFactory.transactionsServiceAPI();
  const currencyAPI = wixContext.serviceFactory.currencyAPI();

  const language = await getPageLanguage({ aspects: req.aspects });
  const url = req.aspects?.get(web.Url);
  let services, categoriesForHeader;

  try {
    const template = await templateListingAPI.getTemplateById(id);
    let reviewsInfo,
      creatorDetails,
      seo,
      languages,
      providerState,
      partnerProfileDetails,
      userReviewForTemplate,
      isEligibleToGiveReview = false;

    const publicTemplate = await templatesAPI.getPublicTemplateById(
      template.templateId,
    );

    if (!publicTemplate) {
      throw Error('publicTemplate not found');
    }

    const hasVeloOrBlocks =
      publicTemplate.template?.publicOptions?.includesCode;

    const [
      _partnerProfileDetails,
      _providerState,
      _isEligibleToGiveReview,
      _services,
      _categoriesForHeader,
    ] = await Promise.allSettled([
      providersAPI.getPartnerProfileById(template!.accountId),
      providerStatesAPI.getProviderState(template!.accountId),
      transactionsServiceAPI.hasTransactionInLastNDays(id, 100),
      servicesAPI.getAllServicesWithHierarchy({
        withPathName: true,
        withTranslatedKey: true,
        language: _.isEmpty(language) ? 'en' : (language as string),
      }),
      servicesAPI.getCategoriesForHeader({
        withPathName: true,
        withTranslatedKey: true,
        language: _.isEmpty(language) ? 'en' : (language as string),
      }),
    ]);

    if (_partnerProfileDetails.status === 'fulfilled') {
      partnerProfileDetails = _partnerProfileDetails.value;
      log('template')
        .withAspects(req.aspects)
        .info('getPartnerDetails fulfilled', {
          partnerProfileDetails,
        });
    } else if (_.isUndefined(partnerProfileDetails)) {
      log('template')
        .withAspects(req.aspects)
        .info('getPartnerDetails rejected', {
          message: _partnerProfileDetails.reason,
        });
    }
    if (_providerState.status === 'fulfilled') {
      providerState = _providerState.value;
    } else {
      log('template').withAspects(req.aspects).info('getProviderState', {
        message: _providerState.reason,
      });
    }
    if (_isEligibleToGiveReview.status === 'fulfilled') {
      isEligibleToGiveReview = _isEligibleToGiveReview.value;
    } else {
      log('template').withAspects(req.aspects).info('getUserTransactions', {
        message: _isEligibleToGiveReview.reason,
      });
    }
    if (_services.status === 'fulfilled') {
      services = _services.value;
    } else {
      log('template')
        .withAspects(req.aspects)
        .info('getAllServicesWithHierarchy', {
          message: _services.reason,
        });
    }
    if (_categoriesForHeader.status === 'fulfilled') {
      categoriesForHeader = _categoriesForHeader.value;
    } else {
      log('template').withAspects(req.aspects).info('getCategoriesForHeader', {
        message: _categoriesForHeader.reason,
      });
    }

    if (!_.isUndefined(template)) {
      const [_languages, reviews] = await Promise.all([
        languagesAPI.getLanguages(),
        reviewsAPI.queryReviews(id),
      ]);

      languages = _languages;
      reviewsInfo = {
        reviews,
        total: template.performance?.reviewsCount ?? 0,
        average: template.performance?.reviewsAverageScore ?? 0,
      };

      const usePublicProfile =
        _.get(providerState, 'statusType') === Type.PUBLISHED ||
        isCreatorInfoLinksToAgencyPageEnabled;

      creatorDetails = {
        slug: _.get(partnerProfileDetails, 'slug'),
        businessWebsite: _.get(
          partnerProfileDetails,
          'professionalInformation.businessWebsite',
        ),
        email: _.get(
          partnerProfileDetails,
          'userDetails.email',
          _.get(partnerProfileDetails, 'professionalInformation.email'),
        ),
        profileImage: _.get(
          partnerProfileDetails,
          'professionalInformation.profileImage',
        ),
        usePublicProfile,
        businessName: _.get(
          partnerProfileDetails,
          'professionalInformation.businessName',
        ),
      };
      const templateSeoAPI = await createTemplateSeoAPI({
        language,
        url,
        reviewsInfo,
        template,
        businessName: creatorDetails.businessName ?? '',
      });
      seo = {
        title: `${template.title} | Wix Marketplace | Wix.com`,
        metaTags: templateSeoAPI.getPageMetaTags(),
        links: templateSeoAPI.getPageLinkTags(),
        structureData: JSON.parse(
          JSON.stringify(templateSeoAPI.getTemplateStructureData()),
        ),
      };
    }

    if (_.isUndefined(template)) {
      return {
        props: {
          error: TEMPLATE_NOT_FOUND,
          seo: {
            title: `Wix Templates Marketplace`,
          },
          services,
          categoriesForHeader,
        },
      };
    }

    try {
      userReviewForTemplate = await reviewsAPI.getUserReviewForTemplate(id);
    } catch (e: any) {
      log('template')
        .withAspects(req.aspects)
        .info('getUserReviewForTemplate', {
          message: e.message,
        });
    }
    const {
      userCurrency,
      currencyConversionRate,
      countryTaxRate,
      isInPriceTaxInclusiveCountry,
    } = await currencyAPI.getGeoBasedTaxAndCurrencyRate({
      aspects: req.aspects,
      loggerName: 'template',
    });

    const { purchaseCount, ...restOfTemplate } = template;

    const props = {
      id,
      template: JSON.parse(
        JSON.stringify({
          ...restOfTemplate,
          platform: EDITOR_TYPES.STUDIO,
          partnerProfileDetails: creatorDetails,
          hasVeloOrBlocks,
        }),
      ),
      reviewsInfo: JSON.parse(JSON.stringify(reviewsInfo)),
      purchaseCount,
      isEligibleToGiveReview,
      userAlreadyLeftReview: !!userReviewForTemplate,
      ...(userReviewForTemplate && { userReviewForTemplate }),
      languages,
      services,
      categoriesForHeader,
      host,
      origin: origin || '',
      entry: entry || '',
      seo,
      userCurrency,
      currencyConversionRate,
      taxRate: countryTaxRate,
      isInPriceTaxInclusiveCountry,
    };

    const initialState = await initialStateService.create();
    return {
      props: {
        initialState,
        ...props,
      },
    };
  } catch (e: any) {
    const { message, stack } = e;
    log('template')
      .withAspects(req.aspects)
      .error(`error w/ template`, { message, stack });
    const initialState = await initialStateService.create();
    return {
      props: {
        initialState,
        message,
        stack,
        seo: {
          title: `Wix Templates Marketplace`,
        },
        error: e.message,
        services,
        categoriesForHeader,
      },
    };
  }
}

export default TemplatePage;
